import {colors, radii, tokens} from '@commercial-helios/ui/theme'
import {Root, Value, Icon, Portal, Viewport, ItemText} from '@radix-ui/react-select'
import {navigate} from 'gatsby'
import Cookies from 'js-cookie'
import React from 'react'
import styled, {css} from 'styled-components'

import LocalizedLink from '@helios/shared/components/localized-link'
import {SelectTrigger, SelectContent, SelectOption, Arrow} from '@helios/shared/components/select'
import {cookiesMap} from '@helios/shared/config/cookies'
import defaultLocales from '@helios/shared/locale/locales'
import type {CountryCode, UrlCode} from '@helios/shared/locale/locales'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useLocale} from '@helios/shared/providers/locale'
import {usePageInfo} from '@helios/shared/providers/page-info'
import {Text, text} from '@helios/shared/styles/typography'
import {ellipsisStyles, focusStyles} from '@helios/shared/styles/utils'
import type Locale from '@helios/shared/types/locale'
import {countryFlags} from '@helios/shared/utils/country-flags'
import mq from '@helios/shared/utils/media-query'

interface LocaleSwitcherProps {
    light?: boolean
    locales?: Locale[]
}

type LocaleSwitcherValue = UrlCode | 'global'

const PLEO_GLOBAL_PAGE_URL = 'https://www.pleo.io/en/global'

const LocaleSwitcher = ({light, locales: customLocales}: LocaleSwitcherProps) => {
    const currentLocale = useLocale()
    const {pageContext} = usePageInfo()
    const globalStrings = useGlobalStrings()
    const locales = customLocales ?? defaultLocales

    const elementOptions = locales.map((locale) => ({
        label: (
            <Option
                country={locale.country}
                countryName={locale.countryName}
                languageName={locale.languageName}
            />
        ),
        value: locale.urlCode,
        urlCode: locale.urlCode,
    }))

    const getDestinationPath = (targetLocaleCode: UrlCode) => {
        const targetLocale = locales.find((locale) => locale.urlCode === targetLocaleCode)

        // If the selected language is prelaunch, we want to redirect them to the landing page
        if (!targetLocale || targetLocale.isPreLaunch) {
            return `/${targetLocaleCode}`
        }

        return pageContext.paths?.[targetLocale.code] ?? `/${targetLocaleCode}`
    }

    const getDestinationHtmlLang = (targetLocaleCode: UrlCode) => {
        const targetLocale = locales.find((locale) => locale.urlCode === targetLocaleCode)

        return targetLocale?.htmlLang
    }

    const handleLocaleSwitch = (newLocale: LocaleSwitcherValue) => {
        if (newLocale === 'global') {
            if (process.env.ENVIRONMENT === 'website') {
                document.documentElement.setAttribute('lang', 'en-gb')
                navigate('/en/global')
            } else {
                window.location.assign(PLEO_GLOBAL_PAGE_URL)
            }
        } else {
            const targetLocale = getDestinationPath(newLocale)
            const targetHtmlLang = getDestinationHtmlLang(newLocale)

            Cookies.set(cookiesMap.defaultCountry, newLocale)

            if (targetHtmlLang) {
                document.documentElement.setAttribute('lang', targetHtmlLang)
            }

            navigate(targetLocale)
        }
    }

    const globalLink = () => {
        if (process.env.ENVIRONMENT === 'website') {
            return (
                <LocalizedLink rawPath to={`/en/global`}>
                    {globalStrings.localeSelectorGlobalLabel}
                </LocalizedLink>
            )
        } else {
            return <a href={PLEO_GLOBAL_PAGE_URL}>{globalStrings.localeSelectorGlobalLabel}</a>
        }
    }

    return (
        <Root
            value={currentLocale.urlCode}
            onValueChange={(newLocale) => handleLocaleSwitch(newLocale as LocaleSwitcherValue)}
        >
            <StyledSelectTrigger $light={light} aria-label={currentLocale.countryName}>
                <Value />

                <Icon>
                    <Arrow />
                </Icon>
            </StyledSelectTrigger>

            <Portal>
                <StyledSelectContent position="popper" $light={light}>
                    <StyledViewport>
                        {elementOptions.map((option) => {
                            const destinationPath = getDestinationPath(option.urlCode)
                            const optionLang = locales.find(
                                (locale) => locale.urlCode === option.urlCode,
                            )

                            return (
                                <StyledSelectOption
                                    $light={light}
                                    key={option.value}
                                    value={option.value}
                                >
                                    <LocalizedLink
                                        rawPath
                                        to={destinationPath}
                                        lang={optionLang?.code}
                                        /**
                                         * We let js handle the navigation in this case,
                                         * so we can set the language cookie and handle keyboard nav.
                                         * We still need to the link for SEO
                                         * */
                                        onClick={(e) => e.preventDefault()}
                                    >
                                        <ItemText>{option.label}</ItemText>
                                    </LocalizedLink>
                                </StyledSelectOption>
                            )
                        })}
                        {globalStrings.localeSelectorGlobalLabel && (
                            <StyledGlobalOption value="global" onClick={(e) => e.preventDefault()}>
                                <ItemText>{globalLink()}</ItemText>
                            </StyledGlobalOption>
                        )}
                    </StyledViewport>
                </StyledSelectContent>
            </Portal>
        </Root>
    )
}

interface OptionProps {
    country: CountryCode
    countryName: string
    languageName: string
}

const Option = ({country, countryName, languageName}: OptionProps) => {
    return (
        <StyledOption>
            <img src={countryFlags[country]} alt="" loading="lazy" width={24} height={16} />

            <StyledOptionCountry>{countryName}</StyledOptionCountry>

            <StyledOptionLanguage variant="label-base" color="lightMuted">
                {languageName}
            </StyledOptionLanguage>
        </StyledOption>
    )
}

interface ListboxStyleProps {
    $light?: boolean
}

const StyledSelectTrigger = styled(SelectTrigger)<ListboxStyleProps>(({$light}) => {
    const lightStyle = css`
        ${text({variant: 'paragraph-large'})};
    `

    return css`
        width: auto;
        display: grid;
        padding: ${tokens.spacing8};
        padding-left: 0;
        cursor: pointer;
        background: none;
        border: none;
        grid-template-columns: auto 1fr;
        column-gap: ${tokens.spacing8};

        &[aria-expanded='true'] {
            box-shadow: none;
        }

        ${$light && lightStyle}

        ${mq.min('md')} {
            column-gap: ${tokens.spacing14};
        }
    `
})

const StyledViewport = styled(Viewport)`
    padding-top: ${tokens.spacing4};
    padding-bottom: ${tokens.spacing4};
`

const StyledSelectOption = styled(SelectOption)<ListboxStyleProps>(({$light}) => {
    const lightStyle = css`
        ${text({variant: 'paragraph-large'})}
    `

    return css`
        &,
        &[data-current] {
            display: grid;
            padding: ${tokens.spacing4} ${tokens.spacing8};

            ${$light && lightStyle}

            a:focus {
                ${focusStyles()}
                border-radius: ${tokens.arc4};
            }
        }
    `
})

const StyledGlobalOption = styled(SelectOption)`
    display: grid;
    padding: ${tokens.spacing12} ${tokens.spacing8};
    color: ${tokens.shade600};
    text-decoration: underline;

    a:focus {
        ${focusStyles()}
        border-radius: ${tokens.arc4};
    }
`

const StyledSelectContent = styled(SelectContent)<ListboxStyleProps>(({$light}) => {
    const lightStyle = css`
        border: none;

        &[data-current] {
            ${text({variant: 'paragraph-large'})}
        }
    `

    return css`
        width: max-content;
        border: solid 1px ${colors.border.secondary};
        border-radius: ${radii.arc8};

        ${$light && lightStyle}

        &:focus {
            ${focusStyles()}
        }

        &:focus-within {
            outline: none;
            box-shadow: ${tokens.shadowRaiseQuiet};
        }
    `
})

const StyledOption = styled.span`
    display: grid;
    grid-template-columns: 24px 1fr;
    align-items: center;
    column-gap: ${tokens.spacing8};
`

const StyledOptionCountry = styled.span`
    ${ellipsisStyles}
`

const StyledOptionLanguage = styled(Text)`
    text-transform: capitalize;
    grid-column: 2;
    display: none;

    ${StyledSelectContent} & {
        display: block;
    }
`

export default LocaleSwitcher
